import { LOCALES, type Locale } from "../domain/types";

const STORAGE_KEY = "flavorcraft:lang";

function isLocale(value: string | null): value is Locale {
  return (LOCALES as readonly string[]).includes(value ?? "");
}

export function localeFromUrl(search: string): Locale | null {
  const value = new URLSearchParams(search).get("lang");
  return isLocale(value) ? value : null;
}

function localeFromStorage(): Locale | null {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    return isLocale(value) ? value : null;
  } catch {
    return null;
  }
}

function localeFromNavigator(): Locale {
  return navigator.language.toLowerCase().startsWith("tr") ? "tr" : "en";
}

export function resolveInitialLocale(search: string): Locale {
  return localeFromUrl(search) ?? localeFromStorage() ?? localeFromNavigator();
}

export function persistLocale(locale: Locale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // localStorage can be unavailable (private browsing, disabled storage).
    // The locale still works for this page load via URL state.
  }
}

// `search` may be "", "?a=1", or "a=1" — URLSearchParams accepts all three.
export function withLocale(search: string, locale: Locale): string {
  const params = new URLSearchParams(search);
  if (locale === "en") params.delete("lang");
  else params.set("lang", locale);
  const query = params.toString();
  return query ? `?${query}` : "";
}
